/**
 * Main App Component
 *
 * Sets up routing, authentication context, and analytics tracking.
 */

import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ParlayProvider } from './contexts/ParlayContext';
import { logPageView } from './firebase';
import { initCrisp } from './crisp';
import Landing from './components/Landing';
import Login from './components/Login';
import Signup from './components/Signup';
import Dashboard from './pages/Dashboard';
import AppLayout from './components/layout/AppLayout';
import Scores from './pages/Scores';
import BoxScore from './pages/BoxScore';
import GameAnalyticsPage from './pages/GameAnalyticsPage';
import Models from './pages/Models';
import Analytics from './pages/Analytics';
import AdminRoute from './components/routes/AdminRoute';

import BetSlips from './pages/BetSlips';
import ParlayWorkshop from './pages/ParlayWorkshop';
import TopPicks from './pages/TopPicks';
import Newsletter from './pages/Newsletter';
import AccountSettings from './pages/AccountSettings';
import BracketSimulator from './pages/BracketSimulator';
import './App.css';

// Analytics page view tracker component
function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    // Map paths to friendly page names
    const getPageName = (pathname) => {
      const routes = {
        '/': 'Landing',
        '/login': 'Login',
        '/signup': 'Signup',
        '/dashboard': 'Dashboard',
        '/models': 'Models',
        '/betslips': 'Bet Slips',
        '/parlay-workshop': 'Parlay Workshop',
        '/top-picks': 'Top Picks',
        '/newsletter': 'Newsletter',
        '/account': 'Account Settings',
        '/bracket': 'Bracket Simulator',
        '/admin': 'Admin Analytics',
      };

      // Check exact matches first
      if (routes[pathname]) return routes[pathname];

      // Check pattern matches
      if (pathname.startsWith('/leagues/') && pathname.includes('/boxscore')) return 'Box Score';
      if (pathname.startsWith('/leagues/') && pathname.includes('/analytics')) return 'Game Analytics';
      if (pathname.startsWith('/leagues/')) return 'Scores';
      if (pathname.startsWith('/newsletter/')) return 'Newsletter';

      return 'Unknown Page';
    };

    logPageView(getPageName(location.pathname), location.pathname);
  }, [location]);

  return null;
}

// Protected Route Component
function ProtectedRoute({ children }) {
  const { currentUser } = useAuth();

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  return children;
}

// Public Route Component (redirect to dashboard if already logged in)
function PublicRoute({ children }) {
  const { currentUser } = useAuth();

  if (currentUser) {
    return <Navigate to="/dashboard" />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Landing page - redirects to dashboard if logged in */}
      <Route
        path="/"
        element={
          <PublicRoute>
            <Landing />
          </PublicRoute>
        }
      />

      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicRoute>
            <Signup />
          </PublicRoute>
        }
      />

      {/* Protected routes with layout */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="leagues" element={<Scores />} />
        <Route path="leagues/:sport" element={<Scores />} />
        <Route path="leagues/:sport/game/:gameId/boxscore" element={<BoxScore />} />
        <Route path="leagues/:sport/game/:gameId/analytics" element={<GameAnalyticsPage />} />
        <Route path="models" element={<Models />} />
        <Route path="betslips" element={<BetSlips />} />
        <Route path="parlay-workshop" element={<ParlayWorkshop />} />
        <Route path="top-picks" element={<TopPicks />} />
        <Route path="newsletter" element={<Newsletter />} />
        <Route path="newsletter/:date" element={<Newsletter />} />
        <Route path="bracket" element={<BracketSimulator />} />
        <Route path="account" element={<AccountSettings />} />
        {/* Admin route - restricted to admin users only */}
        <Route path="admin" element={<AdminRoute><Analytics /></AdminRoute>} />
      </Route>

      {/* 404 fallback */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  // Initialize Crisp chat widget on mount
  useEffect(() => {
    initCrisp();
  }, []);

  return (
    <Router>
      <AnalyticsTracker />
      <AuthProvider>
        <ParlayProvider>
          <AppRoutes />
        </ParlayProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
